import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Position,
  StatCategory,
} from '@sorare/core/src/__generated__/globalTypes';
import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';

import {
  isGameFinished,
  isGameLive,
  isGameStarted,
} from '#src/shared/game/lib.tsx';
import { InlineGame } from '#src/shared/stats/InlineGame/index.tsx';

import { Category } from './Category/index.tsx';
import { DecisiveScore } from './DecisiveScore/index.tsx';
import type { PlayerGameScoreStats_playerGameScoreInterface } from './__generated__/index.graphql.ts';
import {
  GAME_SCORE_TO_SPORT,
  SORTED_STAT_CATEGORIES,
  isCategoryDecisive,
} from './utils.ts';

const Root = styled(Vertical)`
  overflow-x: auto;
  overflow-y: hidden;
`;

const CategoriesList = styled(Vertical)`
  justify-content: stretch;
  overflow: hidden;
  border-radius: var(--double-unit);
  > * {
    overflow: hidden;
  }
  > *:first-child {
    border-top-left-radius: var(--double-unit);
    border-top-right-radius: var(--double-unit);
  }
  > *:last-child {
    border-bottom-left-radius: var(--double-unit);
    border-bottom-right-radius: var(--double-unit);
  }
  > *:not(:first-child) {
    border-top: 1px solid var(--c-nd-200);
  }
`;

const AllAroundScore = ({
  score,
  sport,
}: {
  score: Nullable<number>;
  sport: Sport;
}) => {
  const { formatNumber } = useIntlContext();

  const { color } = findThresholdColor(score ?? 0, sport, 'ALL_AROUND_SCORE');

  return (
    <BodyM as="p" bold color={color}>
      {score ? (
        <FormattedMessage
          {...fantasy.pointsWithStyledScoreWithoutFormat}
          values={{
            score: formatNumber(Number(score.toFixed(2))),
            span: (...chunks) => <span>{chunks}</span>,
          }}
        />
      ) : (
        '-'
      )}
    </BodyM>
  );
};

type Props = {
  playerGameScores: PlayerGameScoreStats_playerGameScoreInterface[];
};

export const PlayerGameScoreStats = ({ playerGameScores }: Props) => {
  const sortedPlayerGameScores = [...playerGameScores].sort(
    (a, b) => a.anyGame.date.getTime() - b.anyGame.date.getTime()
  );

  const liveGamePGS = playerGameScores.find(pGS =>
    isGameLive(pGS.anyGame.statusTyped)
  );
  const bestGamePGS = playerGameScores
    .filter(pgs => isGameFinished(pgs.anyGame.statusTyped))
    .sort((a, b) => b.score - a.score)[0];

  const [playerGameScore, setPlayerGameScore] = useState(
    liveGamePGS ?? bestGamePGS ?? sortedPlayerGameScores[0]
  );

  if (
    playerGameScores.length === 1 &&
    !isGameStarted(playerGameScores[0].anyGame.statusTyped)
  ) {
    return null;
  }

  const allStats = playerGameScore.detailedScore;

  const categories = groupBy(allStats, statScore => statScore.category);
  const sortedCategories = SORTED_STAT_CATEGORIES.filter(category =>
    Object.keys(categories).includes(category)
  );
  const isGoalkeeper = playerGameScore.position === Position.Goalkeeper;
  const filteredCategories = sortedCategories.filter(category => {
    if (isGoalkeeper) {
      return (
        category !== StatCategory.ATTACKING &&
        category !== StatCategory.DEFENDING
      );
    }
    return category !== StatCategory.GOALKEEPING;
  });

  const notDecisiveCategories = filteredCategories.filter(
    category => !isCategoryDecisive(category)
  );

  const hasDecisiveScore =
    (isType(playerGameScore, 'PlayerGameScore') ||
      isType(playerGameScore, 'So5Score')) &&
    playerGameScore.decisiveScore;

  const allAroundScore = playerGameScore.detailedScore
    .filter(statScore =>
      notDecisiveCategories.includes(
        statScore.category as (typeof notDecisiveCategories)[number]
      )
    )
    .reduce((acc, statScore) => acc + statScore.totalScore, 0);

  return (
    <Root gap={2}>
      {sortedPlayerGameScores.length > 1 && (
        <DropdownSelect
          size="small"
          value={playerGameScore.id}
          options={sortedPlayerGameScores.map(pgs => ({
            value: pgs.id,
            label: (
              <InlineGame
                disableTooltip
                inline
                game={pgs.anyGame}
                withGameStatus
                size="sm"
              />
            ),
          }))}
          onChange={value => {
            const selectedPlayerGameScore = sortedPlayerGameScores.find(
              ({ id }) => value === id
            );
            if (selectedPlayerGameScore) {
              setPlayerGameScore(selectedPlayerGameScore);
            }
          }}
        />
      )}
      {!!hasDecisiveScore && (
        <DecisiveScore playerGameScore={playerGameScore} />
      )}
      {!!hasDecisiveScore && (
        <Horizontal>
          <BodyM bold>
            <FormattedMessage {...fantasy.allAroundScore} />
          </BodyM>
          <AllAroundScore
            score={allAroundScore}
            sport={GAME_SCORE_TO_SPORT[playerGameScore.__typename]}
          />
        </Horizontal>
      )}
      <CategoriesList gap={0}>
        {notDecisiveCategories.map(category => (
          <Category
            key={category}
            category={category}
            playerGameScores={[playerGameScore]}
            stats={categories[category] || []}
            defaultExpanded={Object.keys(notDecisiveCategories).length === 1}
          />
        ))}
      </CategoriesList>
    </Root>
  );
};

PlayerGameScoreStats.fragments = {
  playerGameScoreInterface: gql`
    fragment PlayerGameScoreStats_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      score
      position: positionTyped
      anyGame {
        id
        statusTyped
        homeScore
        homeTeam {
          slug
          name
          pictureUrl
        }
        awayScore
        awayTeam {
          slug
          name
          pictureUrl
        }
        ...InlineGame_game
      }
      detailedScore {
        ...Category_statScore
      }
      ... on PlayerGameScore {
        id
        decisiveScore {
          totalScore
        }
      }
      ... on So5Score {
        id
        decisiveScore {
          totalScore
        }
      }
      ...Category_playerGameScoreInterface
      ...DecisiveScore_playerGameScoreInterface
    }
    ${Category.fragments.playerGameScoreInterface}
    ${Category.fragments.statScore}
    ${InlineGame.fragments.game}
    ${DecisiveScore.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<PlayerGameScoreStats_playerGameScoreInterface>,
};
